import { PHONE_TEST_NUMBER } from '@/shared/config/app-config';
import { getFirebaseAuth, isFirebaseConfigured } from '@/shared/lib';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { useTranslate } from '@tolgee/react';
import type { AxiosError } from 'axios';
import { type ConfirmationResult, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { AlertCircle, CheckCircle2, Loader2, MessageSquare, Phone } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { registrationApi } from '../api/registration-api';

type Country = 'FR' | 'BE';

/** Legacy step2: FR/BE mobiles only, in E.164. */
const DIAL_CODES: Record<Country, string> = { FR: '+33', BE: '+32' };

/** "06 12 34 56 78" / "0470 12 34 56" → +33612345678 / +32470123456 */
export function toE164(country: Country, national: string): string | null {
  let digits = national.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  const dial = DIAL_CODES[country].slice(1);
  if (digits.startsWith(dial)) digits = digits.slice(dial.length);
  if (digits.startsWith('0')) digits = digits.slice(1);
  // FR mobiles: 6/7 + 8 digits; BE mobiles: 4 + 8 digits
  const valid = country === 'FR' ? /^[67]\d{8}$/.test(digits) : /^4\d{8}$/.test(digits);
  return valid ? `${DIAL_CODES[country]}${digits}` : null;
}

interface StepPhoneProps {
  /** Already verified (resume): the step is shown as done. */
  verifiedPhone?: string;
  onVerified: (phone: string) => void;
}

export function StepPhone({ verifiedPhone, onVerified }: StepPhoneProps) {
  const { t } = useTranslate();
  const [country, setCountry] = useState<Country>('FR');
  const [national, setNational] = useState('');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<'number' | 'code'>('number');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string>('');
  const confirmationRef = useRef<ConfirmationResult | null>(null);
  const verifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  const resetVerifier = useCallback(() => {
    verifierRef.current?.clear();
    verifierRef.current = null;
  }, []);

  useEffect(() => resetVerifier, [resetVerifier]);

  const e164 = toE164(country, national);
  // Legacy-like test protocol: no SMS, the API checks a fixed code instead
  const isTestNumber = PHONE_TEST_NUMBER !== '' && e164 === PHONE_TEST_NUMBER;

  const firebaseErrorKey = (err: unknown): string => {
    const codeStr = (err as { code?: string })?.code ?? '';
    if (codeStr.includes('invalid-phone-number')) return 'invalidNumber';
    if (codeStr.includes('too-many-requests')) return 'tooManyRequests';
    if (codeStr.includes('invalid-verification-code')) return 'invalidCode';
    if (codeStr.includes('code-expired')) return 'codeExpired';
    if (codeStr.includes('captcha-check-failed')) return 'captcha';
    return 'generic';
  };

  const sendCode = async () => {
    if (!e164) {
      setError(t('signup.phone.errors.invalidNumber'));
      return;
    }
    if (isTestNumber) {
      confirmationRef.current = null;
      setSentTo(e164);
      setPhase('code');
      setCode('');
      setError(null);
      return;
    }
    if (!isFirebaseConfigured()) {
      setError(t('signup.phone.errors.notConfigured'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const auth = getFirebaseAuth();
      if (!verifierRef.current && recaptchaContainerRef.current) {
        verifierRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
          size: 'invisible',
        });
      }
      if (!verifierRef.current) throw new Error('recaptcha container missing');
      confirmationRef.current = await signInWithPhoneNumber(auth, e164, verifierRef.current);
      setSentTo(e164);
      setPhase('code');
      setCode('');
    } catch (err) {
      resetVerifier();
      setError(t(`signup.phone.errors.${firebaseErrorKey(err)}`));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    const testMode = PHONE_TEST_NUMBER !== '' && sentTo === PHONE_TEST_NUMBER;
    if ((!testMode && !confirmationRef.current) || code.trim().length < 4) {
      setError(t('signup.phone.errors.invalidCode'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let idToken: string;
      if (testMode) {
        idToken = `test-phone:${code.trim()}`;
      } else if (confirmationRef.current) {
        const credential = await confirmationRef.current.confirm(code.trim());
        idToken = await credential.user.getIdToken();
      } else {
        throw new Error('no confirmation');
      }
      const result = await registrationApi.verifyPhone(idToken, sentTo);
      onVerified(result.phoneNumber);
    } catch (err) {
      const axiosError = err as AxiosError<{ code?: string; error?: string }>;
      const apiCode = axiosError.response?.data?.code;
      const status = axiosError.response?.status;
      if (apiCode === 'invalid_token') {
        setError(t('signup.phone.errors.invalidCode'));
      } else if (status === 409 || apiCode === 'phone_already_used') {
        setError(t('signup.phone.errors.alreadyUsed'));
      } else if (apiCode === 'unsupported_country') {
        setError(t('signup.phone.errors.unsupportedCountry'));
      } else if (status === 503 || apiCode === 'not_configured') {
        setError(t('signup.phone.errors.notConfigured'));
      } else if (status) {
        setError(t('signup.phone.errors.generic'));
      } else {
        setError(t(`signup.phone.errors.${firebaseErrorKey(err)}`));
      }
    } finally {
      setBusy(false);
    }
  };

  const changeNumber = () => {
    setPhase('number');
    setCode('');
    setError(null);
    confirmationRef.current = null;
    resetVerifier();
  };

  if (verifiedPhone) {
    return (
      <Alert className="border-success/40 bg-success/5">
        <CheckCircle2 className="h-4 w-4 text-success" />
        <AlertDescription>{t('signup.phone.verified', { phone: verifiedPhone })}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Alert className="border-primary/50 bg-primary/5">
        <Phone className="h-4 w-4" />
        <AlertDescription>{t('signup.phone.hint')}</AlertDescription>
      </Alert>

      {phase === 'number' ? (
        <div className="space-y-4">
          <div className="space-y-3">
            <Label className="text-base font-medium">{t('signup.phone.country')}</Label>
            <RadioGroup
              value={country}
              onValueChange={(v) => setCountry(v as Country)}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="FR" id="phone-country-fr" />
                <Label htmlFor="phone-country-fr" className="cursor-pointer">
                  {t('signup.phone.france')}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="BE" id="phone-country-be" />
                <Label htmlFor="phone-country-be" className="cursor-pointer">
                  {t('signup.phone.belgium')}
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone-number">
              {t('signup.phone.number')}
              <span className="text-destructive"> *</span>
            </Label>
            <div className="flex gap-2">
              <span className="inline-flex items-center rounded-md border bg-muted px-3 text-sm text-muted-foreground">
                {DIAL_CODES[country]}
              </span>
              <Input
                id="phone-number"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder={t('signup.phone.numberPlaceholder')}
                value={national}
                onChange={(e) => setNational(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <Button type="button" className="w-full" onClick={sendCode} disabled={busy || !e164}>
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('signup.phone.sending')}
              </>
            ) : (
              <>
                <MessageSquare className="mr-2 h-4 w-4" />
                {t('signup.phone.sendCode')}
              </>
            )}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {PHONE_TEST_NUMBER !== '' && sentTo === PHONE_TEST_NUMBER
              ? t('signup.phone.testNumber', { phone: sentTo })
              : t('signup.phone.codeSent', { phone: sentTo })}
          </p>
          <div className="space-y-2">
            <Label htmlFor="phone-code">
              {t('signup.phone.code')}
              <span className="text-destructive"> *</span>
            </Label>
            <Input
              id="phone-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder={t('signup.phone.codePlaceholder')}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={busy}
              maxLength={6}
            />
          </div>
          <Button
            type="button"
            className="w-full"
            onClick={verifyCode}
            disabled={busy || code.length < 4}
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('signup.phone.verifying')}
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {t('signup.phone.verify')}
              </>
            )}
          </Button>
          <div className="flex justify-between text-sm">
            <button type="button" className="text-primary hover:underline" onClick={changeNumber}>
              {t('signup.phone.changeNumber')}
            </button>
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={sendCode}
              disabled={busy}
            >
              {t('signup.phone.resend')}
            </button>
          </div>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Invisible reCAPTCHA anchor required by Firebase phone auth */}
      <div ref={recaptchaContainerRef} id="recaptcha-container" />
    </div>
  );
}
